declare global {
    interface Window {
        googletag: any;
    }
}

export interface RewardAdCallbacks {
    // Chamado quando o reward ad está pré-carregado e pronto para exibição.
    onAdReady?: (slot: any) => void;
    // Chamado imediatamente antes do reward ad ser exibido ao utilizador.
    onAdShow?: () => void;
    // Chamado quando a recompensa é concedida ao utilizador (após a visualização bem-sucedida do reward ad).
    onRewardGranted?: (payload: any) => void;
    // Chamado quando o reward ad é fechado pelo utilizador.
    onAdClosed?: () => void;
    // Chamado quando o slot do reward ad está vazio.
    onAdEmpty?: () => void;
    // Chamado quando ocorre um erro no reward ad.
    onAdError?: (error: any) => void;
}

export interface RewardAdConfig extends RewardAdCallbacks {
    // O caminho completo da unidade da reward ad.
    adUnitPath: string;
    // Intervalo em segundos para tentar recarregar se o reward ad estiver vazio.
    emptyRetryIntervalSeconds?: number;
    // Indica se o ambiente é de produção para controlar os logs.
    isProdEnv?: boolean;
}

export class RewardAd {
    private adUnitPath: string;
    private _nextAdUnitPath: string | null = null;
    private currentSlot: any | null = null;
    private isAdReady: boolean = false;
    private isAdEmpty: boolean = false;
    private adEventObject: any | null = null;
    private reloadTimer: number | null = null;
    private emptyRetryTimer: number | null = null;
    private readonly EMPTY_RETRY_INTERVAL_SECONDS: number;
    private readonly IS_PROD_ENV: boolean;

    private _onAdReady?: (slot: any) => void;
    private _onRewardGranted?: (payload: any) => void;
    private _onAdClosed?: () => void;
    private _onAdEmpty?: () => void;
    private _onAdError?: (error: any) => void;
    private _onAdShow?: () => void;


    constructor(config: RewardAdConfig) {
        this.adUnitPath = config.adUnitPath || '/22639388115/rewarded_web_example';
        this.EMPTY_RETRY_INTERVAL_SECONDS = config.emptyRetryIntervalSeconds || 5;
        this.IS_PROD_ENV = config.isProdEnv !== undefined ? config.isProdEnv : true;

        this._onAdReady = config.onAdReady;
        this._onRewardGranted = config.onRewardGranted;
        this._onAdClosed = config.onAdClosed;
        this._onAdEmpty = config.onAdEmpty;
        this._onAdError = config.onAdError;
        this._onAdShow = config.onAdShow;

        if (typeof window.googletag === 'undefined' || typeof window.googletag.pubads === 'undefined') {
            this.log('warn', 'googletag não está totalmente carregado. Os listeners serão configurados após googletag.cmd.push.');
        }

        this.setupEventListeners();
    }

    private log(type: 'log' | 'warn' | 'error' = 'log', message: string, error?: any): void {
        if (this.IS_PROD_ENV) {
            return;
        }
        const prefix = '[Sg Reward Ads]';
        switch (type) {
            case 'log':
                console.log(`${prefix} ${message}`);
                break;
            case 'warn':
                console.warn(`${prefix} ${message}`);
                break;
            case 'error':
                console.error(`${prefix} ${message}`, error || '');
                break;
        }
    }

    private setupEventListeners(): void {
        window.googletag.cmd.push(() => {
            const pubads = window.googletag.pubads();

            pubads.addEventListener('slotRenderEnded', (event: any) => {
                if (event.slot !== this.currentSlot) {
                    return;
                }
                this.log('log', `Evento 'slotRenderEnded' em reward ad para slot: ${event.slot.getAdUnitPath()}`);

                if (event.isEmpty) {
                    this.isAdReady = false;
                    this.isAdEmpty = true;
                    this.log('log', 'Slot reward ad renderizado vazio.');
                    this.cleanupTimers();
                    this.emptyRetryTimer = window.setTimeout(() => {
                        this.log('log', `Tentando recarregar anúncio vazio após ${this.EMPTY_RETRY_INTERVAL_SECONDS} segundos.`);
                        this.reloadAd();
                    }, this.EMPTY_RETRY_INTERVAL_SECONDS * 1000);

                    this._onAdEmpty?.();
                } else {
                    this.isAdEmpty = false;
                    this.isAdReady = true;
                    this.adEventObject = event;
                    this.cleanupTimers();
                    this.log('log', 'Slot reward ad renderizado com conteúdo.');
                }
            });

            pubads.addEventListener('rewardedSlotReady', (event: any) => {
                if (event.slot !== this.currentSlot) {
                    return;
                }
                this.isAdReady = true;
                this.adEventObject = event;
                this.log('log', 'Reward ad está pronto para ser exibido.');
                this._onAdReady?.(this.currentSlot);
            });

            pubads.addEventListener('rewardedSlotGranted', (event: any) => {
                if (event.slot !== this.currentSlot) {
                    return;
                }
                this.log('log', 'Reward ad granted: ' + JSON.stringify(event.payload));
                this._onRewardGranted?.(event.payload);
                this.reloadAd();
            });

            pubads.addEventListener('rewardedSlotClosed', (event: any) => {
                if (event.slot !== this.currentSlot) {
                    return;
                }
                this.log('log', 'Reward ad closed.');
                this._onAdClosed?.();
                this.reloadAd();
            });
        });
    }

    private cleanupTimers(): void {
        if (this.reloadTimer) {
            clearTimeout(this.reloadTimer);
            this.reloadTimer = null;
        }
        if (this.emptyRetryTimer) {
            clearTimeout(this.emptyRetryTimer);
            this.emptyRetryTimer = null;
        }
    }

    public loadAd(): void {
        this.cleanupTimers();

        const adPathToLoad = this._nextAdUnitPath || this.adUnitPath;

        if (this.currentSlot && this.currentSlot.getAdUnitPath() === adPathToLoad) {
            this.log('log', 'Slot existente com o mesmo AdUnitPath, iniciando recarregamento...');
            this.reloadAd();
            return;
        }
        
        if (this.currentSlot) {
            this.log('log', `Slot existente com AdUnitPath diferente (${this.currentSlot.getAdUnitPath()} vs ${adPathToLoad}), destruindo antes de carregar novo.`);
            
            this.destroy(false);
            
            setTimeout(() => {
                this.create(adPathToLoad);
            }, 100);
            return;
        }

        this.create(adPathToLoad);
    }

    private reloadAd(): void {
        this.log('log', 'Recarregando reward ad...');
        this.cleanupTimers();

        const adPathToReload = this._nextAdUnitPath || this.adUnitPath;

        if (!this.currentSlot || this.currentSlot.getAdUnitPath() !== adPathToReload) {
            this.log('log', 'Nenhum slot atual ou AdUnitPath diferente para destruir, carregando novo.');
            this.loadAd();
            return;
        }

        this.destroy(false);

        if (this._nextAdUnitPath) {
            this.adUnitPath = this._nextAdUnitPath;
            this._nextAdUnitPath = null;
            this.log('log', `Usando o próximo ad unit path para recarregar: ${this.adUnitPath}`);
        }

        this.reloadTimer = window.setTimeout(() => {
            this.create(adPathToReload);
        }, 200);
    }

    private create(adPath: string): void {
        window.googletag.cmd.push(() => {
            try {
                if (this._nextAdUnitPath) {
                    this.adUnitPath = this._nextAdUnitPath;
                    this._nextAdUnitPath = null;
                    this.log('log', `Usando o próximo ad unit path: ${this.adUnitPath}`);
                }

                const rewardedSlotObject = window.googletag.defineOutOfPageSlot(
                    this.adUnitPath,
                    window.googletag.enums.OutOfPageFormat.REWARDED
                );

                if (!rewardedSlotObject) {
                    this.log('error', 'Falha ao definir o slot de reward ad.');
                    this._onAdError?.(new Error('Failed to define rewarded ad slot.'));
                    return;
                }

                this.currentSlot = rewardedSlotObject;
                this.currentSlot.addService(window.googletag.pubads());

                const slotId = this.currentSlot.getSlotElementId();
                window.googletag.display(slotId);
                window.googletag.pubads().refresh([this.currentSlot]);

                this.log('log', `Slot "${this.adUnitPath}" criado e solicitado (pré-carregado).`);

            } catch (e) {
                this.log('error', 'Erro ao carregar reward ad:', e);
                this._onAdError?.(e);
            }
        });
    }

    public destroy(clearCallbacks: boolean = true): void {
        this.cleanupTimers();
        
        if (this.currentSlot) {
            window.googletag.cmd.push(() => {
                try {
                    window.googletag.destroySlots([this.currentSlot]);
                    this.log('log', `Destruindo slot: ${this.currentSlot.getAdUnitPath()}`);
                    this.currentSlot = null;
                    this.isAdReady = false;
                    this.isAdEmpty = false;
                    this.adEventObject = null;
                } catch (e) {
                    this.log('error', 'Erro ao destruir slot:', e);
                }
            });
        }
        
        if (clearCallbacks) {
            this._onAdReady = undefined;
            this._onAdShow = undefined;
            this._onRewardGranted = undefined;
            this._onAdClosed = undefined;
            this._onAdEmpty = undefined;
            this._onAdError = undefined;
            this.log('log', 'RewardAd completamente destruído - timers, slots e callbacks limpos.');
        } else {
            this.log('log', 'RewardAd slot destruído - timers e slots limpos, callbacks mantidos.');
        }
    }

    public show(): boolean {
        if (!this.currentSlot || !this.isAdReady || this.isAdEmpty) {
            this.log('log', 'Anúncio não pode ser exibido: slot não definido, não pronto ou vazio.');
            if (this.isAdEmpty) {
                this._onAdEmpty?.();
            } else {
                this._onAdError?.(new Error('Ad not ready or empty for display.'));
            }
            return false;
        }

        try {
            this.adEventObject.makeRewardedVisible();
            this.log('log', 'makeRewardedVisible chamado. Anúncio solicitado para exibição.');
            this._onAdShow?.();
            return true;
        } catch (e) {
            this.log('error', 'Erro ao tentar exibir o anúncio com makeRewardedVisible:', e);
            this._onAdError?.(e);
            return false;
        }
    }

    public nextAd(path: string | null): void {
        this._nextAdUnitPath = path;
        this.log('log', `Próximo AdUnitPath definido para: ${path || 'null'}`);
    }

    public get IsReady(): boolean {
        return this.isAdReady;
    }

    public get IsEmpty(): boolean {
        return this.isAdEmpty;
    }
}